/* línkè · 壁纸（2026-07-04 聊天版；2026-07-12 升级多作用域 + 位置调整）
   一张图（dataURL）+ 遮罩浓度 + 模糊开关 + 位置变换，存 localStorage。
   作用域：'chat'（聊天壁纸，沿用 v1 老键零迁移）/ 'home'（星洲首页背景）。
   刻意不上服务器：手机竖屏/电脑横屏比例不同，每台设备存自己的壁纸才对。 */

const SCOPE_KEYS = {
  chat: {
    img: 'lingke.chat.wallpaper.img.v1',
    dim: 'lingke.chat.wallpaper.dim.v1',
    blur: 'lingke.chat.wallpaper.blur.v1',
    pos: 'lingke.chat.wallpaper.pos.v1',
  },
  home: {
    img: 'lingke.home.wallpaper.img.v1',
    dim: 'lingke.home.wallpaper.dim.v1',
    blur: 'lingke.home.wallpaper.blur.v1',
    pos: 'lingke.home.wallpaper.pos.v1',
  },
};
// 顶栏同色（2026-07-04，聊天专属）：
//   TOPRGB = 壁纸【原图顶部条】的平均色（"r,g,b"，不含遮罩，采样一次存着）。
//   TOP    = 上者与「当前主题遮罩色×遮罩浓度」合成后的【肉眼顶部色】（#hex），
//            供 index.html 首帧脚本同步读取（首帧读不到 CSS 变量，只能靠缓存）。
const KEY_TOPRGB = 'lingke.chat.wallpaper.toprgb.v1';
const KEY_TOP = 'lingke.chat.wallpaper.top.v1';

export const WALLPAPER_DEFAULT_DIM = 55; // 默认遮罩 55%：图看得见、字也读得清

/* ---------- 位置变换（2026-07-12）：铃的「图对不上屏、想拖一拖」 ----------
   pos = { x, y, scale }：x/y = object-position 百分比（50/50 居中），
   scale = 在 cover 基础上再放大（1~3）。纯数据，渲染统一走 wallpaperImgStyle。 */
export const DEFAULT_POS = { x: 50, y: 50, scale: 1 };

/** 把任意来路的 pos 掰回合法范围（纯函数，进单测）。 */
export function clampPos(p) {
  const n = (v, lo, hi, dft) => {
    const f = typeof v === 'string' ? parseFloat(v) : v;
    return Number.isFinite(f) ? Math.min(hi, Math.max(lo, f)) : dft;
  };
  return {
    x: n(p?.x, 0, 100, DEFAULT_POS.x),
    y: n(p?.y, 0, 100, DEFAULT_POS.y),
    scale: n(p?.scale, 1, 3, DEFAULT_POS.scale),
  };
}

/** 壁纸 <img> 的行内样式（渲染与调整器共用同一份数学，所见即所得）。
    模糊态在 scale 上再乘 1.08：把模糊后的透明边缘顶出屏外（原 CSS .blurred 同款）。 */
export function wallpaperImgStyle(pos, blurred = false) {
  const p = clampPos(pos || DEFAULT_POS);
  const s = p.scale * (blurred ? 1.08 : 1);
  return {
    objectPosition: `${p.x}% ${p.y}%`,
    transform: s === 1 ? 'none' : `scale(${s})`,
    transformOrigin: `${p.x}% ${p.y}%`,
  };
}

function keysFor(scope) {
  return SCOPE_KEYS[scope] || SCOPE_KEYS.chat;
}

export function loadWallpaperFor(scope) {
  const K = keysFor(scope);
  try {
    const img = localStorage.getItem(K.img) || null;
    const dimRaw = parseInt(localStorage.getItem(K.dim) ?? '', 10);
    const dim = Number.isFinite(dimRaw)
      ? Math.min(90, Math.max(0, dimRaw))
      : WALLPAPER_DEFAULT_DIM;
    const blur = localStorage.getItem(K.blur) === '1';
    let pos = DEFAULT_POS;
    try { pos = clampPos(JSON.parse(localStorage.getItem(K.pos) || 'null')); } catch {}
    return { img, dim, blur, pos };
  } catch {
    return { img: null, dim: WALLPAPER_DEFAULT_DIM, blur: false, pos: DEFAULT_POS };
  }
}

/** 只写传进来的字段（滑条拖动时别反复重写整张图）。返回错误文案或 null。
    换图 / 撤图时顺手清位置（新图从居中开始才符合直觉）。 */
export function saveWallpaperPatchFor(scope, patch) {
  const K = keysFor(scope);
  try {
    if ('img' in patch) {
      if (patch.img) localStorage.setItem(K.img, patch.img);
      else localStorage.removeItem(K.img);
      localStorage.removeItem(K.pos);
    }
    if ('dim' in patch) localStorage.setItem(K.dim, String(patch.dim));
    if ('blur' in patch) localStorage.setItem(K.blur, patch.blur ? '1' : '0');
    if ('pos' in patch) {
      if (patch.pos) localStorage.setItem(K.pos, JSON.stringify(clampPos(patch.pos)));
      else localStorage.removeItem(K.pos);
    }
    return null;
  } catch (e) {
    return e && e.name === 'QuotaExceededError'
      ? '这张图压完还是太大，这台设备的本地空间放不下 —— 换一张试试'
      : '壁纸没存住：' + (e?.message || e);
  }
}

/* —— 聊天壁纸旧接口（Chat.jsx 等存量调用点零改动）—— */
export function loadWallpaper() {
  return loadWallpaperFor('chat');
}
export function saveWallpaperPatch(patch) {
  return saveWallpaperPatchFor('chat', patch);
}

/* ================= 顶栏同色（方案A，2026-07-04） =================
   iOS standalone 的状态栏只吃纯色 theme-color、塞不进图；网页顶端又有一条
   .app-shell::before 的 --page-bg 色带盖住壁纸顶 → 加壁纸后最顶像分成两截。
   解法：采样壁纸顶部平均色，与当前主题遮罩合成成「肉眼顶部色」，写进
   theme-color + --wp-top（给那条色带改填这个色）→ 状态栏·色带·壁纸三段同色无缝。 */

/** 采样壁纸原图顶部约 12% 的平均色，返回 [r,g,b] 或 null（异步，画布） */
export function sampleImageTopColor(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!w || !h) return resolve(null);
          const cw = 32;
          const ch = Math.max(1, Math.min(64, Math.round((cw * h) / w)));
          const cv = document.createElement('canvas');
          cv.width = cw;
          cv.height = ch;
          const ctx = cv.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, cw, ch);
          const rows = Math.max(1, Math.round(ch * 0.12)); // 顶部 ~12%
          const d = ctx.getImageData(0, 0, cw, rows).data;
          let r = 0, g = 0, b = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
          }
          if (!n) return resolve(null);
          resolve([Math.round(r / n), Math.round(g / n), Math.round(b / n)]);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch {
      resolve(null);
    }
  });
}

export function loadTopRgb() {
  try {
    const raw = localStorage.getItem(KEY_TOPRGB);
    if (!raw) return null;
    const p = raw.split(',').map((x) => parseInt(x, 10));
    return p.length === 3 && p.every(Number.isFinite) ? p : null;
  } catch {
    return null;
  }
}

export function saveTopRgb(rgb) {
  try {
    if (rgb && rgb.length === 3) localStorage.setItem(KEY_TOPRGB, rgb.join(','));
    else localStorage.removeItem(KEY_TOPRGB);
  } catch {}
}

function toHex(n) {
  const v = Math.min(255, Math.max(0, Math.round(n)));
  return v.toString(16).padStart(2, '0');
}

function readVoidRgb() {
  try {
    const s = getComputedStyle(document.documentElement)
      .getPropertyValue('--void-rgb')
      .trim();
    const p = s.split(/[\s,]+/).map((x) => parseInt(x, 10));
    if (p.length === 3 && p.every(Number.isFinite)) return p;
  } catch {}
  return [21, 16, 42]; // 夜空兜底
}

/**
 * 壁纸在场时，把「肉眼顶部色」写进 <html> 的 data-wallpaper + --wp-top，
 * 并缓存到 localStorage 供首帧脚本读；无壁纸/没采样过则清掉。返回 #hex 或 null。
 * 合成：veil 盖在图上，opacity=dim/100 → 顶色 = void×a + imgTop×(1-a)。
 */
export function applyWallpaperTopColor() {
  const root = document.documentElement;
  const { img, dim } = loadWallpaper();
  const raw = loadTopRgb();
  if (!img || !raw) {
    root.removeAttribute('data-wallpaper');
    root.style.removeProperty('--wp-top');
    try { localStorage.removeItem(KEY_TOP); } catch {}
    return null;
  }
  const a = Math.min(90, Math.max(0, dim)) / 100;
  const v = readVoidRgb();
  const hex =
    '#' +
    toHex(v[0] * a + raw[0] * (1 - a)) +
    toHex(v[1] * a + raw[1] * (1 - a)) +
    toHex(v[2] * a + raw[2] * (1 - a));
  root.setAttribute('data-wallpaper', '1');
  root.style.setProperty('--wp-top', hex);
  try { localStorage.setItem(KEY_TOP, hex); } catch {}
  return hex;
}
